import { useTranslation } from "react-i18next";
import { useEscapeKey } from "../../../../hooks/useEscapeKey";

interface ControlTowerModalProps {
  onClose: () => void;
  themeClasses: {
    cardBg: string;
    cardBorder: string;
    text: string;
    textSecondary: string;
    buttonHover: string;
  };
}

export default function ControlTowerModal({
  onClose,
  themeClasses,
}: ControlTowerModalProps) {
  useEscapeKey(onClose);
  const { t } = useTranslation("lite");

  const features = [
    {
      icon: "ri-user-settings-line",
      color: "blue",
      title: t("controlTower.driverAssignmentTitle"),
      desc: t("controlTower.driverAssignmentDesc"),
    },
    {
      icon: "ri-smartphone-line",
      color: "purple",
      title: t("controlTower.mobileAppTitle"),
      desc: t("controlTower.mobileAppDesc"),
    },
    {
      icon: "ri-shield-check-line",
      color: "primary-500",
      title: t("controlTower.virtualCustodyTitle"),
      desc: t("controlTower.virtualCustodyDesc"),
    },
    {
      icon: "ri-alarm-warning-line",
      color: "red",
      title: t("controlTower.securityProtocolsTitle"),
      desc: t("controlTower.securityProtocolsDesc"),
    },
  ];

  const additionalFeatures = [
    t("controlTower.advancedReports"),
    t("controlTower.geofencing"),
    t("controlTower.customAlerts"),
    t("controlTower.externalIntegration"),
    t("controlTower.support"),
    t("controlTower.training"),
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div
        className={`${themeClasses.cardBg} rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border ${themeClasses.cardBorder} shadow-2xl`}
      >
        {/* Header */}
        <div className="p-6 border-b border-primary-500/20 bg-gradient-to-r from-primary-500/10 to-transparent">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-400 rounded-xl flex items-center justify-center">
                <i className="ri-shield-check-line text-white text-xl"></i>
              </div>
              <div>
                <h2 className={`text-xl font-bold ${themeClasses.text}`}>
                  {t("controlTower.title")}
                </h2>
                <p className={`text-sm ${themeClasses.textSecondary}`}>
                  {t("controlTower.subtitle")}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className={`p-2 ${themeClasses.buttonHover} rounded-lg transition-colors`}
            >
              <i className={`ri-close-line text-xl ${themeClasses.text}`}></i>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className={`p-4 ${themeClasses.cardBg} rounded-xl border ${themeClasses.cardBorder}`}
              >
                <div className="flex items-center space-x-3 mb-3">
                  <div
                    className={`w-10 h-10 bg-${feature.color}-500/10 rounded-lg flex items-center justify-center`}
                  >
                    <i
                      className={`${feature.icon} text-${feature.color}-500 text-lg`}
                    ></i>
                  </div>
                  <h3 className={`font-semibold ${themeClasses.text}`}>
                    {feature.title}
                  </h3>
                </div>
                <p className={`text-sm ${themeClasses.textSecondary}`}>
                  {feature.desc}
                </p>
              </div>
            ))}
          </div>

          <div
            className={`p-4 ${themeClasses.cardBg} rounded-xl border ${themeClasses.cardBorder} mb-6`}
          >
            <h3 className={`font-semibold ${themeClasses.text} mb-3`}>
              {t("controlTower.additionalFeaturesTitle")}
            </h3>
            <div className="grid grid-cols-2 gap-2">
              {additionalFeatures.map((feature, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <i className="ri-check-line text-primary-500 text-sm"></i>
                  <span className={`text-sm ${themeClasses.textSecondary}`}>
                    {feature}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Price and buttons */}
          <div
            className={`p-6 bg-gradient-to-r from-primary-500/10 to-transparent rounded-xl border ${themeClasses.cardBorder} mb-6`}
          >
            <div className="text-center">
              <div className="flex items-center justify-center space-x-2 mb-2">
                <span className={`text-3xl font-bold ${themeClasses.text}`}>
                  {t("controlTower.price")}
                </span>
                <span className={`text-lg ${themeClasses.textSecondary}`}>
                  {t("controlTower.currency")}
                </span>
              </div>
              <p className={`text-sm ${themeClasses.textSecondary} mb-1`}>
                {t("controlTower.specialPrice")}
              </p>
              <p className={`text-xs ${themeClasses.textSecondary}`}>
                {t("controlTower.noLimits")}
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <button className="flex-1 bg-primary-500 hover:bg-primary-400 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 flex items-center justify-center space-x-2">
              <i className="ri-rocket-line text-lg"></i>
              <span>{t("controlTower.activate")}</span>
            </button>
            <button
              className={`flex-1 ${themeClasses.cardBg} border ${themeClasses.cardBorder} ${themeClasses.text} font-semibold py-3 px-6 rounded-xl transition-all duration-200 flex items-center justify-center space-x-2 ${themeClasses.buttonHover}`}
            >
              <i className="ri-play-circle-line text-lg"></i>
              <span>{t("controlTower.requestDemo")}</span>
            </button>
          </div>

          <div className="mt-6 text-center">
            <p className={`text-sm ${themeClasses.textSecondary} mb-2`}>
              {t("controlTower.needMoreInfo")}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="tel:+525555555555"
                className="flex items-center space-x-2 text-primary-500 hover:text-primary-400"
              >
                <i className="ri-phone-line"></i>
                <span className="text-sm font-medium">+52 55 5555 5555</span>
              </a>
              <a
                href="mailto:ventas@company.com"
                className="flex items-center space-x-2 text-primary-500 hover:text-primary-400"
              >
                <i className="ri-mail-line"></i>
                <span className="text-sm font-medium">ventas@company.com</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
